<template>
  <div :v-if="isOpen">
    <Modal :is-open="isOpen">
      <template #form>
        <h3 class="text-2xl font-medium leading-10 text-left text-gray-600 dark:text-white">
          {{ $t("Filter Records") }}
          <Funnel class="float-right w-6 h-6 text-blue-light" aria-hidden="true" />
        </h3>
        <p class="text-sm text-gray-500">
          {{ $t("You can here submit a filter to narrow down your records, in case you have outdated and not used records") }}
        </p>
        <div class="flex flex-col justify-between flex-1">
          <div class="divide-y divide-gray-200">
            <div class="pb-5">
              <div class="mt-2">
                <div class="flex flex-row-reverse gap-x-2">
                  <div
                    class="overflow-hidden relative p-2 rounded-md dark:border-dark-divider-border flex flex-col border-[1px] border-gray-300 w-1/2">
                    <div
                      v-for="item in objects"
                      v-show="item.name == current_object_opened_filter?.name"
                      :key="item.name"
                      class="inset-0 w-full h-full translate-x-1">
                      <div :class="[filter_error ? 'border-red-400' : '']" class="relative w-full" style="min-height: 15rem">
                        <VerticalAdvancedMetricsFilter
                          :label="$t('Filter') + ` ${item.plural_label}`"
                          :show-clear-all="false"
                          :show-buttons="false"
                          :object-type="
                            current_object_opened_filter?.name == 'Account' ? 'company' : current_object_opened_filter?.name.toLowerCase()
                          "
                          :placeholder="$t('Search for metrics for filter with')"
                          :disable-add="false"
                          @reset="
                            ($event) => {
                              resetObjectFilter(current_object_opened_filter?.name)
                            }
                          "
                          @change="
                            ($event) => {
                              if (!$event.valid) {
                                resetObjectFilter(current_object_opened_filter?.name)
                              } else {
                                setObjectFilter(current_object_opened_filter?.name, $event)
                              }
                            }
                          "
                          @filter="
                            ($event) => {
                              setObjectFilter(current_object_opened_filter?.name, $event)
                            }
                          " />
                      </div>

                      <label
                        class="px-1 text-base font-medium text-gray-600 transition-all rounded-md text-md peer-placeholder-shown:text-base dark:text-dark-body-text peer-focus:font-normal bg-light-label-background dark:bg-dark-label-background">Limit (Optional)</label>
                      <TextInput
                        v-if="filters[item?.name]?.filter"
                        :value="filters[item?.name].filter.limit"
                        :name="item?.name"
                        label=""
                        classes="w-full relative dark:text-white"
                        :placeholder="$t('Limit the number of the synced records, default: No limit')"
                        :is-box-background="true"
                        :show-label="true"
                        @input="
                          ($event) => {
                            filters[item?.name].filter.limit = $event.target.value
                          }
                        " />
                    </div>
                  </div>
                  <div class="w-1/2">
                    <div class="flex flex-col flex-grow h-full pt-2 pb-2 overflow-y-auto bg-white border-r border-gray-200">
                      <div class="flex items-center flex-shrink-0 space-y-5 font-bold">
                        {{ $t("Mapped Objects") }}
                      </div>
                      <div class="flex flex-col flex-grow mt-5">
                        <nav class="flex-1 space-y-1 bg-white" aria-label="Sidebar">
                          <div
                            v-for="item in objects"
                            :key="item.name"
                            :class="[
                              item.name == current_object_opened_filter.name
                                ? 'bg-blue-50 border-blue text-blue'
                                : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                              'cursor-pointer group flex items-center justify-between px-3 py-2 text-sm font-medium border-l-4'
                            ]"
                            @click="
                              () => {
                                current_object_opened_filter = item
                                if (!filters[item?.name]) {
                                  resetObjectFilter(current_object_opened_filter?.name)
                                }
                              }
                            ">
                            <span>{{ item.label }}</span>
                            <span v-if="filters[item.name]?.is_valid"
                                  class="text-blue-light">{{ $t("Filter Applied") }} <span class="hidden">{{ $t("Est.") }} {{ filters[item.name].count }}</span></span>
                            <span v-if="filters[item.name]?.filter?.length > 1 && !filters[item.name]?.is_valid" class="text-red-500">{{
                              $t("Filter Error")
                            }}</span>
                          </div>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-row justify-between w-full gap-2 mt-2">
          <div
            class="w-1/2 m-0 cancel-btn"
            @click="
              () => {
                $emit('update:isOpen', false)
              }
            ">
            {{ $t("Cancel") }}
          </div>
          <button type="button" class="w-1/2 primary-btn" @click="submitFilter">{{ $t("Continue") }}</button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import VerticalAdvancedMetricsFilter from "@/components/shared/AdvancedMetricsFilterVertical.vue"
import Modal from "@/components/shared/Modal.vue"
import { Funnel } from "lucide-vue-next"
import TextInput from "@/components/shared/TextInput.vue"

export default {
  name: "DataMappingObjectsFilter",
  components: {
    VerticalAdvancedMetricsFilter,
    Modal,
    TextInput,
    Funnel
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    objects: {
      type: Array,
      required: true
    }
  },
  emits: ["update:object", "submitFilters", "update:isOpen"],
  data() {
    return {
      current_object_opened_filter: null,
      filters: {},
      filter_error: null
    }
  },
  watch: {
    isOpen: {
      immediate: true,
      handler(val) {
        if (val && !this.current_object_opened_filter) {
          this.current_object_opened_filter = this.objects[0]
          Array.from(new Set(this.objects.filter((s) => s.is_mapped).map((o) => o.name))).forEach((object_name) => {
            this.filters[object_name] = {
              filter: { type: "filter", limit: null, expression: null },
              count: "~",
              is_valid: false
            }
          })
        }
      }
    }
  },
  created() {},
  methods: {
    submitFilter() {
      this.$emit("submitFilters", this.filters)
    },
    setObjectFilter(name, event) {
      if (this.filters[name]) {
        this.filters[name]["filter"]["expression"] = event?.filter
        this.filters[name]["is_valid"] = true
      } else {
        this.filters[name] = {
          filter: { type: "filter", limit: null, expression: event?.filter },
          count: "~",
          is_valid: true
        }
      }
    },
    resetObjectFilter(name) {
      if (this.filters[name]?.filter) {
        this.filters[name]["filter"]["expression"] = null
        this.filters[name]["is_valid"] = false
      } else {
        this.filters[name] = {
          filter: { type: "filter", limit: null, expression: null },
          count: "~",
          is_valid: false
        }
      }
    }
  }
}
</script>
